import axios, { AxiosRequestConfig, CancelToken, AxiosResponse } from 'axios';
import HttpStatus from 'http-status';
import { ThunkDispatch } from 'redux-thunk';
import { STATUS } from '../Constants/Actions.constants';
import { INeoAction } from '../store/Actions.interface';
import { NeoState } from '../store';
import { IErrorResponse } from '~/Models/DTO/Error.models';
import { USER_SERVICE_URLS } from '~/Constants/Urls.constants';
import { extractErrorMessage } from './Error.helper';
import {
  getLocalStorageAccessToken,
  getLocalStorageExpirationToken,
  getLocalStorageRefreshToken,
  getLocalStorageProviderUUId,
  logout,
} from '~/Hooks/';
import { AuthModel } from '~/Models/DTO/Auth.model';
import { objectToFormData } from './Url.helpers';

export type IMethod =
  | 'GET'
  | 'DELETE'
  | 'HEAD'
  | 'OPTIONS'
  | 'POST'
  | 'PUT'
  | 'PATCH'
  | 'LINK'
  | 'UNLINK';

interface IRequestOptions {
  url: string;
  state: NeoState;
  dispatch: ThunkDispatch<NeoState, unknown, INeoAction>;
  query?: Record<string, boolean | string | number | undefined>;
  config?: AxiosRequestConfig;
  apiVersion?: string;
  data?: any;
  cancelToken?: CancelToken;
  actionName?: string;
  request?: any;
  secondTry?: boolean;
  customToken?: string;
}

type getCredentialsResult = {
  userToken: string | null;
  providerId?: string;
};
export const getCredentials = async (): Promise<getCredentialsResult> => {
  // Set default token
  let userToken = getLocalStorageAccessToken();
  const providerId = getLocalStorageProviderUUId() || '';

  const userTokenExpiresAt = getLocalStorageExpirationToken();
  // Access token expired
  if (userTokenExpiresAt && userTokenExpiresAt - new Date().getTime() < 5000) {
    const updatedToken = await getRefreshedToken();
    if (updatedToken) {
      userToken = updatedToken;
    }
  }

  return { userToken, providerId };
};

const getHeaders = (
  providerId?: string,
  userToken?: string,
  apiVersion?: string,
  customHeaders?: any,
  configHeaders?: any,
) => ({
  'Content-Type': 'application/json',
  Accept: 'application/json',
  'app-version': '1.0.0',
  'app-id': 'NeoPro',
  ...(providerId ? { ProviderId: providerId } : {}),
  ...(userToken ? { Authorization: `Bearer ${userToken}` } : {}),
  ...(apiVersion ? { 'api-version': apiVersion } : {}),
  ...(customHeaders ? customHeaders : {}),
  ...(configHeaders || {}),
});

const requestRestfulApi = async <T>(requestOptions: IRequestOptions): Promise<AxiosResponse<T>> => {
  const {
    actionName = '',
    url = '',
    config = {},
    query,
    dispatch,
    apiVersion,
    cancelToken,
    request,
    data,
    customToken,
  } = requestOptions;

  dispatchLoading(dispatch, actionName);

  const { userToken, providerId } = await getCredentials();

  const { method = 'GET', headers: customHeaders } = config;
  const headers = getHeaders(
    providerId,
    customToken || userToken || undefined,
    apiVersion,
    customHeaders,
    config.headers,
  );

  const shouldAddQuery = query && !url?.includes('?');
  const queryString = shouldAddQuery ? new URLSearchParams(query as Record<string, string>) : '';

  const sanitizedData = data || config.data;

  const requestParams: AxiosRequestConfig = {
    method,
    data: sanitizedData,
    url: `${url}${shouldAddQuery ? '?' : ''}${queryString}`,
    headers,
    cancelToken,
  };

  try {
    const response = await axios.request<T>(requestParams);
    const responseData = response.data;
    if (response.status === HttpStatus.UNAUTHORIZED) {
      dispatchUnauthorized(dispatch, actionName, responseData, request);
      return response;
    }
    dispatchSuccess(dispatch, actionName, responseData, request);
    return response;
  } catch (err) {
    if ((err as any)?.response?.status === HttpStatus.UNAUTHORIZED) {
      // check if token has expired or we got a correct unauthorized
      await CheckIfTokenExpired(dispatch, requestOptions, actionName, err, request);
    } else {
      dispatchFailure(dispatch, actionName, err as any, request, requestParams);
    }
    return (err as any)?.response;
  }
};

export const dispatchLoading = (
  dispatch: ThunkDispatch<NeoState, unknown, INeoAction>,
  actionName: string,
) => {
  if (!actionName) {
    return;
  }
  dispatch({
    name: actionName,
    type: `${actionName}_${STATUS.LOADING}`,
    status: STATUS.LOADING,
  });
};

export const dispatchSuccess = (
  dispatch: ThunkDispatch<NeoState, unknown, INeoAction>,
  actionName: string,
  payload: any,
  request: any,
) => {
  if (!actionName) {
    return;
  }
  dispatch({
    name: actionName,
    status: STATUS.SUCCESS,
    type: `${actionName}_${STATUS.SUCCESS}`,
    request,
    payload,
  });
};

const dispatchUnauthorized = <T>(
  dispatch: ThunkDispatch<NeoState, unknown, INeoAction>,
  actionName: string,
  exception: string | T,
  request?: any,
) => {
  if (!actionName) {
    return;
  }
  dispatch({
    name: actionName,
    status: STATUS.UNAUTHORIZED,
    type: `${actionName}_${STATUS.UNAUTHORIZED}`,
    exception,
    request,
  });
};
export const dispatchFailure = (
  dispatch: ThunkDispatch<NeoState, unknown, INeoAction>,
  actionName: string,
  err: IErrorResponse,
  request: any,
  requestParams: any,
) => {
  if (!actionName) {
    return;
  }

  dispatch({
    name: actionName,
    type: `${actionName}_${STATUS.FAILURE}`,
    status: STATUS.FAILURE,
    exception: extractErrorMessage(err),
    params: {
      url: URL,
      ...requestParams,
    },
    payload: err,
    request,
  });
};

export default requestRestfulApi;

const CheckIfTokenExpired = async (
  dispatch: ThunkDispatch<NeoState, unknown, INeoAction>,
  requestOptions: IRequestOptions,
  actionName: string,
  err: any,
  request: any,
) => {
  const checkError = await axios
    .request({
      method: 'GET',
      url: USER_SERVICE_URLS.MY_ACCOUNT,
    })
    .catch(async (error) => {
      if (error?.response?.status === HttpStatus.UNAUTHORIZED) {
        const newToken = await getRefreshedToken();
        if (newToken) {
          requestRestfulApi({
            ...requestOptions,
            customToken: newToken,
          });
        }
      }
    });
  if (checkError && checkError.status !== 401) {
    //  || actionName === REQUEST_ACTIONS.LOGIN) {
    dispatchUnauthorized(dispatch, actionName, err?.message, request);
  }
};

const getRefreshedToken = async () => {
  try {
    const refreshToken = getLocalStorageRefreshToken();
    if (refreshToken) {
      const response = await refreshAccessToken(refreshToken);
      if (!response?.access_token) {
        throw new Error('no access token was returned while attempting to refresh the token.');
      } else {
        return response?.access_token;
      }
    }
  } catch (err) {
    logout();
  }
  return undefined;
};

const refreshAccessToken = async (refreshToken: string) => {
  const response = await axios.request<AuthModel>({
    method: 'POST',
    url: USER_SERVICE_URLS.ACCESS_TOKEN,
    data: objectToFormData({
      grant_type: 'refresh_token',
      client_id: 'pro',
      refresh_token: refreshToken,
    }),
  });
  return response.data;
};
